import { AxiosRequestConfig } from "axios";
import { ActionCreator, UnknownAction } from "redux";
import { ForkEffect, call, put, takeLatest } from "redux-saga/effects";

interface IRequestAction<P = unknown> {
  payload: P;
  type: string;
}

interface IEditRequestPayload {
  body?: unknown;
  config?: AxiosRequestConfig;
  data?: unknown;
  id: number;
}

type EditAction = IRequestAction<IEditRequestPayload>;

export function createSagaFactory(type: "create" | "get" | "edit" | "delete") {
  return function createWatcher<TResponse, PPayload = unknown>(
    requestType: string,
    apiFunction: (first: PPayload, second?: unknown, third?: AxiosRequestConfig) => Promise<{ data: TResponse }>,
    successAction: (data: TResponse) => UnknownAction,
    failureAction: ActionCreator<UnknownAction>,
    options?: {
      callback?: (payload: IRequestAction<PPayload>["payload"] | IEditRequestPayload) => void;
      redirectLink?: string;
      isReturnPayload?: boolean;
    },
  ): () => Generator<ForkEffect, void, unknown> {
    const isGet = type === "get";
    const isEdit = type === "edit";
    const isRemove = type === "delete";

    function* fetchSaga(action: IRequestAction<PPayload> | EditAction) {
      try {
        const response: { data: TResponse } = yield call(
          apiFunction,
          (isEdit
            ? (action as EditAction).payload.id
            : isGet
              ? ((action as IRequestAction<PPayload>).payload as PPayload)
              : (action as IRequestAction<PPayload>).payload) as PPayload,
          isEdit
            ? ((action as EditAction).payload.data ?? (action as EditAction).payload.body)
            : undefined,
          isEdit ? (action as EditAction).payload.config : undefined,
        );

        const successPayload = (
          isRemove
            ? (action as IRequestAction<PPayload>).payload
            : options?.isReturnPayload
              ? { data: response.data, request_data: (action as IRequestAction<PPayload>).payload }
              : response.data
        ) as TResponse;

        yield put(successAction(successPayload));

        options?.callback?.(
          (isEdit ? (action as EditAction).payload : (action as IRequestAction<PPayload>).payload) as
            | IRequestAction<PPayload>["payload"]
            | IEditRequestPayload,
        );

        if (options?.redirectLink) {
          window.location.replace(options.redirectLink);
        }
      } catch (error) {
        yield put(failureAction(error));
      }
    }

    return function* watch() {
      yield takeLatest(requestType, fetchSaga);
    };
  };
}

export const getFetchSaga = createSagaFactory("get");
export const editFetchSaga = createSagaFactory("edit");
export const createFetchSaga = createSagaFactory("create");
export const removeFetchSaga = createSagaFactory("delete");
